import { I18n } from '@calendly/i18n';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { IconButton } from '@calendly/ui/components/icon-button';
import { ArrowChevronDownMiniIcon, ArrowChevronUpMiniIcon } from '@calendly/ui/components/icons';
import { Loader } from '@calendly/ui/components/loader';
import { Group, GroupTitle } from '@calendly/ui/components/menu';
import { OptionUI } from '@calendly/ui/components/select';
import { TitleText } from '@calendly/ui/components/typography';
import { colorIconInfo } from '@calendly/ui/theme';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { selectAll as selectAllCalendarAccounts, selectById as selectCalendarConnectionById, useDisablePushMutation, useFetchCalendarAccountsQuery, useUpdateCalendarAccountMutation } from 'features/calendar_connections';
import { Actions, Card, CardButton, Container as CardContainer } from '../../integration_card';
import { ProviderHeader, ProviderLogo } from '../header';
import { MeetingIntelligenceDisclaimer } from './meeting_intelligence_disclaimer';
const thinCardCss = "t1ko10hl";
const ThinCard = forwardRef((props, ref) => <Card ref={ref} {...props} className={classNames(thinCardCss, props.className)} />);
const dropdownContainerCss = "dt3djue";
const DropdownContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(dropdownContainerCss, props.className)} />;
});
const ExpandButtonIcon = ({
  isOpen
}) => isOpen ? <ArrowChevronUpMiniIcon color={colorIconInfo} /> : <ArrowChevronDownMiniIcon color={colorIconInfo} />;
const CalendarItem = ({
  pushAccountUuid,
  calendarId,
  selected,
  children
}) => {
  const [updateAccount] = useUpdateCalendarAccountMutation({
    fixedCacheKey: 'update-account-push-calendar'
  });
  const [disablePush] = useDisablePushMutation({
    fixedCacheKey: 'disable-account-push-calendar'
  });
  const onClickHandler = useCallback(e => {
    if (!calendarId && !pushAccountUuid) {
      return e;
    }
    if (!calendarId) {
      return disablePush({
        calendarAccountUUID: pushAccountUuid
      });
    }
    return updateAccount({
      id: pushAccountUuid,
      pushCalendarId: calendarId
    });
  }, [pushAccountUuid, calendarId, updateAccount, disablePush]);
  return <OptionUI onClick={onClickHandler} selected={selected}>
      {children}
    </OptionUI>;
};
const DropdownMenu = ({
  pushAccountUuid,
  pushCalendarId
}) => {
  const {
    data
  } = useFetchCalendarAccountsQuery();
  const accounts = selectAllCalendarAccounts(data);
  return <ViewportDropdownMenu id="calendar-list" role="group" viewportOffset={{
    top: 180
  }} fullWidth pullTop>
      {accounts.filter(({
      blocked
    }) => !blocked).map(({
      uuid,
      identifier,
      calendars
    }) => <Group key={uuid}>
          <GroupTitle>
            <TitleText level={3} variant="eyebrow">
              {identifier}
            </TitleText>
          </GroupTitle>
          {(calendars ?? []).filter(({
        writeAccess
      }) => writeAccess).map(({
        id,
        name
      }) => <CalendarItem key={id} pushAccountUuid={uuid} calendarId={id} selected={uuid === pushAccountUuid && id === pushCalendarId}>
              {name}
            </CalendarItem>)}
        </Group>)}
      <Group>
        <CalendarItem pushAccountUuid={pushAccountUuid} calendarId={null}>
          {I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.do_not_add_events')}
        </CalendarItem>
      </Group>
    </ViewportDropdownMenu>;
};
export const PushCalendarsCardDropdown = ({
  pushAccountUuid
}) => {
  const [toggle, Dropdown, isOpen] = useDropdown();
  const {
    data
  } = useFetchCalendarAccountsQuery();
  const [_updateAccount, {
    isLoading: isUpdateLoading
  }] = useUpdateCalendarAccountMutation({
    fixedCacheKey: 'update-account-push-calendar'
  });
  const [_disablePush, {
    isLoading: isDisableLoading
  }] = useDisablePushMutation({
    fixedCacheKey: 'disable-account-push-calendar'
  });
  const {
    kind,
    identifier,
    calendars,
    pushCalendarId
  } = pushAccountUuid ? selectCalendarConnectionById(data, pushAccountUuid) : {
    kind: null,
    calendars: [],
    pushCalendarId: null
  };
  const {
    name: pushCalendarName
  } = calendars?.find(calendar => calendar.id === pushCalendarId) || {};
  const toggleDropdown = useCallback(({
    target
  }) => {
    toggle();
    target.blur();
  }, [toggle]);
  const expandButtonTitle = isOpen ? I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.hide_calendars') : I18n.t('publisher.availability_settings.section.calendar_settings.adding_events.show_calendars');
  const isLoading = isUpdateLoading || isDisableLoading;
  return <>
      <TitleText level={3} size="small">
        <label id="adding-events-label" htmlFor="adding-events">
          {I18n.t('publisher.availability_page.calendar_settings.adding_events.title')}
        </label>
      </TitleText>
      <DropdownContainer>
        <CardContainer>
          <ThinCard>
            <CardButton id="adding-events" onClick={toggleDropdown} role="combobox" aria-expanded="false" aria-labelledby="adding-events-label" aria-controls="calendar-list" disabled={isLoading} />
            <ProviderLogo provider={kind} />
            <ProviderHeader provider={kind} title={pushCalendarName} subtitle={identifier} />
            <Actions>
              {isLoading ? <Loader size="small" /> : <IconButton size="small" onClick={toggleDropdown} title={expandButtonTitle}>
                  <ExpandButtonIcon isOpen={isOpen} />
                </IconButton>}
            </Actions>
          </ThinCard>
        </CardContainer>
        <Dropdown>
          <DropdownMenu pushAccountUuid={pushAccountUuid} pushCalendarId={pushCalendarId} />
        </Dropdown>
      </DropdownContainer>
      <MeetingIntelligenceDisclaimer />
    </>;
};

require("./push_calendars_card_dropdown.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./push_calendars_card_dropdown.js");